document.addEventListener('DOMContentLoaded', () => {
  const setupHomeHeroSliders = (root = document) => {
    const sliders = root.querySelectorAll('[data-home-hero-slider]');
    sliders.forEach((slider) => {
      if (slider.dataset.sliderReady === 'true') return;
      slider.dataset.sliderReady = 'true';

      const slides = Array.from(slider.querySelectorAll('[data-home-hero-slide]'));
      const prevButton = slider.querySelector('[data-home-hero-prev]');
      const nextButton = slider.querySelector('[data-home-hero-next]');
      if (slides.length < 2 || !prevButton || !nextButton) return;

      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      let autoplayTimer = null;

      const updateSlider = (nextIndex) => {
        activeIndex = (nextIndex + slides.length) % slides.length;

        slides.forEach((slide, index) => {
          const isActive = index === activeIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

          const video = slide.querySelector('video');
          if (!video) return;

          if (isActive) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      };

      const startAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = window.setInterval(() => {
          updateSlider(activeIndex + 1);
        }, 5000);
      };

      const goToSlide = (nextIndex) => {
        updateSlider(nextIndex);
        startAutoplay();
      };

      prevButton.addEventListener('click', () => goToSlide(activeIndex - 1));
      nextButton.addEventListener('click', () => goToSlide(activeIndex + 1));
      updateSlider(activeIndex);
      startAutoplay();
    });
  };

  const setupFreshScrollSections = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-fresh-scroll-section]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-fresh-scroll-section]'))
    ];
    if (!sections.length) return;

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const updateSections = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const progress = clamp((viewportHeight - rect.top) / (viewportHeight * .78), 0, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const scale = .84 + (eased * .12);

        section.style.setProperty('--fresh-scroll-scale', scale.toFixed(3));
      });
    };

    let frame = null;
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        updateSections();
      });
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    updateSections();

    const canUsePointerEffects = window.matchMedia('(hover: hover) and (pointer: fine)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!canUsePointerEffects) return;

    sections.forEach((section) => {
      if (section.dataset.freshPointerReady === 'true') return;
      section.dataset.freshPointerReady = 'true';

      const cards = section.querySelectorAll('.home-fresh-arrived__card');
      cards.forEach((card) => {
        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) - .5;
          const y = ((event.clientY - rect.top) / rect.height) - .5;

          card.classList.add('is-pointer-active');
          card.style.setProperty('--fresh-card-rotate-x', `${(-y * 4).toFixed(2)}deg`);
          card.style.setProperty('--fresh-card-rotate-y', `${(x * 5).toFixed(2)}deg`);
        });

        card.addEventListener('pointerleave', () => {
          card.classList.remove('is-pointer-active');
          card.style.setProperty('--fresh-card-rotate-x', '0deg');
          card.style.setProperty('--fresh-card-rotate-y', '0deg');
        });
      });
    });
  };

  const setupHomeReviews = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-home-reviews]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-home-reviews]'))
    ];

    sections.forEach((section) => {
      if (section.dataset.reviewsReady === 'true') return;
      section.dataset.reviewsReady = 'true';

      const prevButton = section.querySelector('[data-home-reviews-prev]');
      const nextButton = section.querySelector('[data-home-reviews-next]');
      const track = section.querySelector('[data-home-reviews-track]');
      const viewport = section.querySelector('.home-reviews__viewport');
      if (!prevButton || !nextButton || !track) return;

      let offset = 0;
      const shiftReviews = (direction) => {
        const firstCard = track.querySelector('.home-reviews__card');
        const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 320;
        if (window.matchMedia('(max-width: 749px)').matches && viewport) {
          viewport.scrollBy({ left: direction * -Math.round(cardWidth + 14), behavior: 'smooth' });
          return;
        }
        offset += direction * Math.round(cardWidth * 0.8);
        section.style.setProperty('--reviews-offset', `${offset}px`);
      };

      prevButton.addEventListener('click', () => shiftReviews(1));
      nextButton.addEventListener('click', () => shiftReviews(-1));
    });
  };

  const setupHomeInstagramReels = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-home-instagram-reels]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-home-instagram-reels]'))
    ];

    sections.forEach((section) => {
      if (section.dataset.instagramReelsReady === 'true') return;

      const viewport = section.querySelector('[data-home-instagram-reels-viewport]');
      const track = section.querySelector('[data-home-instagram-reels-track]');
      const prevButton = section.querySelector('[data-home-instagram-reels-prev]');
      const nextButton = section.querySelector('[data-home-instagram-reels-next]');
      const cards = Array.from(section.querySelectorAll('[data-home-instagram-reels-card]'));
      const videos = Array.from(section.querySelectorAll('[data-home-instagram-reel-video]'));
      const soundButtons = Array.from(section.querySelectorAll('[data-home-instagram-reel-sound]'));
      if (!viewport || !track || !cards.length) return;

      section.dataset.instagramReelsReady = 'true';

      const getStep = () => {
        const cardWidth = cards[0].getBoundingClientRect().width;
        const trackStyles = window.getComputedStyle(track);
        const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
        return Math.round(cardWidth + gap);
      };

      const updateControls = () => {
        if (!prevButton || !nextButton) return;
        const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        prevButton.disabled = viewport.scrollLeft <= 2;
        nextButton.disabled = viewport.scrollLeft >= maxScrollLeft - 2;
      };

      prevButton?.addEventListener('click', () => {
        viewport.scrollBy({ left: -getStep(), behavior: 'smooth' });
      });

      nextButton?.addEventListener('click', () => {
        viewport.scrollBy({ left: getStep(), behavior: 'smooth' });
      });

      viewport.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        viewport.scrollBy({ left: event.key === 'ArrowLeft' ? -getStep() : getStep(), behavior: 'smooth' });
      });

      viewport.addEventListener('scroll', updateControls, { passive: true });
      window.addEventListener('resize', updateControls);

      if ('IntersectionObserver' in window && videos.length) {
        const videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        }, { root: viewport, threshold: 0.55 });

        videos.forEach((video) => videoObserver.observe(video));
      } else if (videos[0]) {
        videos[0].play().catch(() => {});
      }

      const syncSoundButton = (button, video) => {
        const isSoundOn = !video.muted;
        button.classList.toggle('is-sound-on', isSoundOn);
        button.setAttribute('aria-pressed', isSoundOn ? 'true' : 'false');
        button.setAttribute('aria-label', isSoundOn ? 'Turn Reel sound off' : 'Turn Reel sound on');
      };

      soundButtons.forEach((button, index) => {
        const video = videos[index];
        if (!video) return;

        button.addEventListener('click', () => {
          const shouldEnableSound = video.muted;

          videos.forEach((otherVideo, otherIndex) => {
            otherVideo.muted = true;
            if (soundButtons[otherIndex]) syncSoundButton(soundButtons[otherIndex], otherVideo);
          });

          video.muted = !shouldEnableSound;
          if (shouldEnableSound) {
            video.volume = 1;
            video.play().catch(() => {});
          }
          syncSoundButton(button, video);
        });

        syncSoundButton(button, video);
      });

      updateControls();
    });
  };

  const setupHomeValues = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-home-values]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-home-values]'))
    ];
    if (!sections.length) return;

    const canUsePointerEffects = window.matchMedia('(hover: hover) and (pointer: fine)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!canUsePointerEffects) return;

    sections.forEach((section) => {
      if (section.dataset.valuesReady === 'true') return;
      section.dataset.valuesReady = 'true';

      section.querySelectorAll('.home-values__item').forEach((item) => {
        item.addEventListener('pointermove', (event) => {
          const rect = item.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) - .5;
          const y = ((event.clientY - rect.top) / rect.height) - .5;
          const glowX = ((x + .5) * 100).toFixed(1);
          const glowY = ((y + .5) * 100).toFixed(1);

          item.classList.add('is-pointer-active');
          item.style.setProperty('--value-rotate-x', `${(-y * 4).toFixed(2)}deg`);
          item.style.setProperty('--value-rotate-y', `${(x * 5).toFixed(2)}deg`);
          item.style.setProperty('--value-glow-x', `${glowX}%`);
          item.style.setProperty('--value-glow-y', `${glowY}%`);
        });

        item.addEventListener('pointerleave', () => {
          item.classList.remove('is-pointer-active');
          item.style.setProperty('--value-rotate-x', '0deg');
          item.style.setProperty('--value-rotate-y', '0deg');
          item.style.setProperty('--value-glow-x', '50%');
          item.style.setProperty('--value-glow-y', '45%');
        });
      });
    });
  };

  const setupInsideStory = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-inside-story]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-inside-story]'))
    ];

    if (!sections.length) return;

    const canUsePointerEffects = window.matchMedia('(hover: hover) and (pointer: fine)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!canUsePointerEffects) return;

    sections.forEach((section) => {
      if (section.dataset.insideStoryReady === 'true') return;
      section.dataset.insideStoryReady = 'true';

      section.querySelectorAll('.inside-brand__card, .inside-brand__process-card, .inside-brand__standard-card').forEach((card) => {
        card.addEventListener('pointermove', (event) => {
          const rect = card.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) - .5;
          const y = ((event.clientY - rect.top) / rect.height) - .5;

          card.classList.add('is-pointer-active');
          card.style.transform = `translateY(-5px) rotateX(${(-y * 2.5).toFixed(2)}deg) rotateY(${(x * 3).toFixed(2)}deg)`;
        });

        card.addEventListener('pointerleave', () => {
          card.classList.remove('is-pointer-active');
          card.style.transform = '';
        });
      });
    });
  };

  const setupHomeVideoStories = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-home-video-story]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-home-video-story]'))
    ];

    sections.forEach((section) => {
      if (section.dataset.videoStoryReady === 'true') return;
      section.dataset.videoStoryReady = 'true';

      const video = section.querySelector('[data-home-video-story-video]');
      const soundButton = section.querySelector('[data-home-video-story-sound]');
      if (!video || !soundButton) return;

      const syncSoundState = () => {
        const isSoundOn = !video.muted;
        soundButton.classList.toggle('is-sound-on', isSoundOn);
        soundButton.setAttribute('aria-pressed', isSoundOn ? 'true' : 'false');
        soundButton.setAttribute('aria-label', isSoundOn ? 'Turn sound off' : 'Turn sound on');
      };

      soundButton.addEventListener('click', () => {
        video.muted = !video.muted;
        if (!video.muted) video.volume = 1;
        video.play().catch(() => {});
        syncSoundState();
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        }, { threshold: 0.35 });

        observer.observe(section);
      } else {
        video.play().catch(() => {});
      }

      syncSoundState();
    });
  };

  const setupShopProductSearches = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-shop-product-search-section]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-shop-product-search-section]'))
    ];

    sections.forEach((section) => {
      if (section.dataset.productSearchReady === 'true') return;
      section.dataset.productSearchReady = 'true';

      const form = section.querySelector('[data-shop-product-search]');
      const input = section.querySelector('[data-shop-product-search-input]');
      const results = section.querySelector('[data-shop-product-search-results]');
      if (!form || !input || !results) return;

      const products = Array.from(section.querySelectorAll('[data-shop-product-card]')).map((card) => {
        const titleLink = card.querySelector('.shop-product-card__title');
        const image = card.querySelector('.shop-product-card__media img');
        const reviews = card.querySelector('.shop-product-card__reviews');
        const price = card.querySelector('.shop-product-card__sale-price');
        const description = card.querySelector('.shop-product-card__description');
        const subtitle = card.querySelector('.shop-product-card__subtitle');
        const keywords = [
          titleLink?.textContent,
          subtitle?.textContent,
          description?.textContent,
          card.dataset.shopProductKeywords,
          'essential oil',
          'product',
          'zova'
        ].filter(Boolean).join(' ');

        return {
          title: titleLink?.textContent?.trim() || '',
          url: titleLink?.href || '#',
          image: image?.currentSrc || image?.src || '',
          reviews: reviews?.textContent?.replace(/\s+/g, ' ').trim() || '',
          price: price?.textContent?.replace(/\s+/g, ' ').trim() || '',
          keywords: keywords.toLowerCase()
        };
      }).filter((product) => product.title);

      const escapeHtml = (value) => value.replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[character]);

      const closeResults = () => {
        results.hidden = true;
        results.innerHTML = '';
      };

      const renderResults = () => {
        const query = input.value.trim().toLowerCase();
        if (!query) {
          closeResults();
          return;
        }

        const words = query.split(/\s+/).filter(Boolean);
        const matches = products
          .map((product) => {
            const title = product.title.toLowerCase();
            const exactTitle = title.includes(query) ? 3 : 0;
            const allWords = words.every((word) => product.keywords.includes(word)) ? 2 : 0;
            const anyWord = words.some((word) => product.keywords.includes(word)) ? 1 : 0;
            return { product, score: exactTitle + allWords + anyWord };
          })
          .filter((item) => item.score > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 4)
          .map((item) => item.product);

        if (!matches.length) {
          closeResults();
          return;
        }

        results.innerHTML = matches.map((product) => `
          <a class="shop-product-cards__search-result" href="${escapeHtml(product.url)}">
            <img src="${escapeHtml(product.image)}" alt="" loading="lazy">
            <span>
              <span class="shop-product-cards__search-result-title">${escapeHtml(product.title)}</span>
              <span class="shop-product-cards__search-result-reviews">${escapeHtml(product.reviews)}</span>
            </span>
            <span class="shop-product-cards__search-result-price">${escapeHtml(product.price)}</span>
          </a>
        `).join('');
        results.hidden = false;
      };

      input.addEventListener('input', renderResults);
      input.addEventListener('focus', renderResults);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeResults();
          input.blur();
        }
      });

      form.addEventListener('submit', () => {
        closeResults();
      });

      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) closeResults();
      });
    });
  };

  const setupShopCategoryFilters = (root = document) => {
    const sections = [
      ...(root.matches?.('[data-shop-product-search-section]') ? [root] : []),
      ...Array.from(root.querySelectorAll('[data-shop-product-search-section]'))
    ];

    sections.forEach((section) => {
      if (section.dataset.categoryFilterReady === 'true') return;

      const filter = section.querySelector('[data-shop-category-filter]');
      const layout = section.querySelector('.shop-product-cards__layout');
      const status = section.querySelector('[data-shop-category-status]');
      const buttons = Array.from(section.querySelectorAll('[data-shop-category]'));
      const cards = Array.from(section.querySelectorAll('[data-shop-product-card]'));
      if (!filter || !layout || !buttons.length || !cards.length) return;

      section.dataset.categoryFilterReady = 'true';

      const activateCategory = (category) => {
        let visibleCount = 0;

        cards.forEach((card) => {
          const isVisible = category === 'all' || card.dataset.shopProductCategory === category;
          card.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        buttons.forEach((button) => {
          const isActive = button.dataset.shopCategory === category;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        layout.dataset.visibleCount = String(visibleCount);
        if (status) {
          const activeButton = buttons.find((button) => button.dataset.shopCategory === category);
          const label = activeButton?.dataset.shopCategoryLabel || 'Products';
          status.textContent = `${label}: ${visibleCount} product${visibleCount === 1 ? '' : 's'} displayed.`;
        }
      };

      buttons.forEach((button) => {
        button.addEventListener('click', () => activateCategory(button.dataset.shopCategory));
      });

      activateCategory('all');
    });
  };

  const setupZovaFaq = (root = document) => {
    const sections = root.querySelectorAll('[data-zova-faq]');
    sections.forEach((section) => {
      if (section.dataset.faqReady === 'true') return;
      section.dataset.faqReady = 'true';

      const tabs = Array.from(section.querySelectorAll('[data-faq-tab]'));
      const panels = Array.from(section.querySelectorAll('[data-faq-panel]'));

      const openFirstItem = (panel) => {
        const items = Array.from(panel.querySelectorAll('.zova-faq-item'));
        items.forEach((item, index) => {
          const button = item.querySelector('.zova-faq-item__question');
          const isOpen = index === 0;
          item.classList.toggle('is-open', isOpen);
          if (button) button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      };

      const activateTab = (key, shouldFocus = false) => {
        tabs.forEach((tab) => {
          const isActive = tab.dataset.faqTab === key;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive && shouldFocus) tab.focus();
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.faqPanel === key;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
          if (isActive) openFirstItem(panel);
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activateTab(tab.dataset.faqTab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activateTab(tabs[nextIndex].dataset.faqTab, true);
        });
      });

      section.querySelectorAll('.zova-faq-item__question').forEach((button) => {
        button.addEventListener('click', () => {
          const item = button.closest('.zova-faq-item');
          const panel = button.closest('[data-faq-panel]');
          if (!item || !panel) return;
          const shouldOpen = !item.classList.contains('is-open');

          panel.querySelectorAll('.zova-faq-item').forEach((sibling) => {
            const siblingButton = sibling.querySelector('.zova-faq-item__question');
            sibling.classList.remove('is-open');
            if (siblingButton) siblingButton.setAttribute('aria-expanded', 'false');
          });

          item.classList.toggle('is-open', shouldOpen);
          button.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
        });
      });
    });
  };

  const setupShopShowcaseGalleries = (root = document) => {
    const galleries = root.querySelectorAll('[data-shop-showcase-gallery]');
    galleries.forEach((gallery) => {
      if (gallery.dataset.galleryReady === 'true') return;
      gallery.dataset.galleryReady = 'true';

    const viewport = gallery.querySelector('[data-shop-showcase-viewport]');
    const track = gallery.querySelector('[data-shop-showcase-track]');
    const prevButton = gallery.querySelector('[data-shop-showcase-prev]');
    const nextButton = gallery.querySelector('[data-shop-showcase-next]');
    const section = gallery.closest('.shop-showcase');
    const lightbox = section?.querySelector('[data-shop-showcase-lightbox]');
    const lightboxContent = section?.querySelector('[data-shop-showcase-lightbox-content]');
    const lightboxClose = section?.querySelector('[data-shop-showcase-lightbox-close]');
    if (!viewport || !track || !prevButton || !nextButton) return;

    const getItems = () => Array.from(track.querySelectorAll('[data-shop-showcase-item]'));

    const updateGallery = () => {
      const items = getItems();
      const visibleCount = window.innerWidth <= 1100 ? 2 : 4;
      if (!items.length) {
        nextButton.disabled = true;
        return;
      }

      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const hasOverflow = items.length > visibleCount;
      prevButton.classList.toggle('is-hidden', !hasOverflow || viewport.scrollLeft <= 2);
      nextButton.disabled = !hasOverflow;
    };

    prevButton.addEventListener('click', () => {
      viewport.scrollBy({ left: -viewport.clientWidth, behavior: 'smooth' });
      window.setTimeout(updateGallery, 360);
    });

    nextButton.addEventListener('click', () => {
      const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      if (viewport.scrollLeft >= maxScrollLeft - 2) {
        viewport.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        viewport.scrollBy({ left: viewport.clientWidth, behavior: 'smooth' });
      }
      window.setTimeout(updateGallery, 360);
    });

    if (lightbox && lightboxContent && lightboxClose) {
      const closeLightbox = () => {
        lightbox.hidden = true;
        lightboxContent.innerHTML = '';
      };

      const openLightboxFromItem = (item) => {
        const media = item?.querySelector('img, video');
        if (!media) return;

        lightboxContent.innerHTML = '';
        const clone = media.cloneNode(true);
        if (clone.tagName === 'VIDEO') {
          clone.controls = true;
          clone.autoplay = true;
          clone.loop = true;
          clone.muted = false;
          clone.playsInline = true;
        }

        lightboxContent.appendChild(clone);
        lightbox.hidden = false;
      };

      getItems().forEach((item) => {
        if (item.dataset.lightboxBound === 'true') return;
        item.dataset.lightboxBound = 'true';
        item.addEventListener('dblclick', () => openLightboxFromItem(item));
      });

      track.addEventListener('dblclick', (event) => {
        const item = event.target.closest('[data-shop-showcase-item]');
        if (!item) return;
        openLightboxFromItem(item);
      });

      lightboxClose.addEventListener('click', closeLightbox);
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }

    viewport.addEventListener('scroll', updateGallery);
    window.addEventListener('resize', updateGallery);
    updateGallery();
  });
  };

  const setupReachForms = (root = document) => {
    const forms = root.querySelectorAll('.reach-form');
    forms.forEach((form) => {
      if (form.dataset.reachReady === 'true') return;
      form.dataset.reachReady = 'true';

      const contactInput = form.querySelector('[data-reach-contact-field]');
      const messageInput = form.querySelector('[data-reach-message-field]');
      const emailField = form.querySelector('[data-reach-email-field]');
      const phoneField = form.querySelector('[data-reach-phone-field]');
      const submitButton = form.querySelector('[data-reach-submit]');
      const hint = form.querySelector('[data-reach-contact-hint]');
      const defaultEmail = emailField?.value || '';
      const defaultHint = hint?.textContent || '';

      if (!contactInput || !messageInput || !emailField || !phoneField || !submitButton) return;

      const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      const isValidIndianPhone = (value) => /^\d{10}$/.test(value);

      const updateReachForm = () => {
        const contactValue = contactInput.value.trim();
        const messageValue = messageInput.value.trim();
        const validEmail = isValidEmail(contactValue);
        const validPhone = isValidIndianPhone(contactValue);
        const hasContact = contactValue.length > 0;
        const hasMessage = messageValue.length > 0;
        const canSubmit = hasMessage && (validEmail || validPhone);

        emailField.value = validEmail ? contactValue : defaultEmail;
        phoneField.value = validPhone ? contactValue : '';
        submitButton.disabled = !canSubmit;
        contactInput.classList.toggle('is-invalid', hasContact && !validEmail && !validPhone);
        contactInput.setCustomValidity(hasContact && !validEmail && !validPhone
          ? 'Enter a valid email or a 10 digit Indian mobile number.'
          : '');
        contactInput.setAttribute('aria-invalid', hasContact && !validEmail && !validPhone ? 'true' : 'false');

        if (hint) {
          hint.classList.toggle('is-error', hasContact && !validEmail && !validPhone);
          hint.textContent = hasContact && !validEmail && !validPhone
            ? 'Enter a valid email or a 10 digit Indian mobile number.'
            : defaultHint;
        }
      };

      form.addEventListener('submit', (event) => {
        updateReachForm();
        if (submitButton.disabled) {
          event.preventDefault();
          form.reportValidity();
        }
      });
      contactInput.addEventListener('input', updateReachForm);
      messageInput.addEventListener('input', updateReachForm);
      updateReachForm();
    });
  };

  const setupCartDiscountForms = (root = document) => {
    const forms = root.querySelectorAll('[data-cart-discount-form]');
    forms.forEach((form) => {
      if (form.dataset.discountReady === 'true') return;
      form.dataset.discountReady = 'true';

      const input = form.querySelector('input[name="discount"]');
      const button = form.querySelector('button[type="submit"]');
      const status = form.querySelector('[data-cart-discount-status]');
      if (!input || !button || !status) return;

      const baseDiscount = Number(form.dataset.currentTotalDiscount || '0');
      const cartUrlRoot = window.Shopify?.routes?.root || '/';
      const cartUpdateUrl = `${cartUrlRoot}cart/update.js`;

      const normalize = (value) => value.trim().toLowerCase();

      const cartHasDiscountCode = (cart, code) => {
        const normalizedCode = normalize(code);
        const cartApplications = Array.isArray(cart.cart_level_discount_applications)
          ? cart.cart_level_discount_applications
          : [];
        const lineApplications = Array.isArray(cart.items)
          ? cart.items.reduce((discounts, item) => discounts.concat(Array.isArray(item.discounts) ? item.discounts : []), [])
          : [];
        const applications = [...cartApplications, ...lineApplications];

        const matchesCode = applications.some((application) => {
          const title = normalize(application.title || '');
          const appCode = normalize(application.code || '');
          return title === normalizedCode || appCode === normalizedCode;
        });

        return matchesCode || Number(cart.total_discount || 0) > baseDiscount;
      };

      const setStatus = (message, isError = false) => {
        status.textContent = message;
        status.hidden = false;
        status.classList.toggle('is-error', isError);
      };

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const code = input.value.trim();
        if (!code) {
          setStatus('Sorry, this code is not available.', true);
          return;
        }

        button.disabled = true;
        setStatus('Checking code...');

        try {
          const response = await fetch(cartUpdateUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ discount: code })
          });

          const cart = await response.json();
          if (!response.ok || !cartHasDiscountCode(cart, code)) {
            setStatus('Sorry, this code is not available.', true);
            button.disabled = false;
            return;
          }

          window.location.reload();
        } catch (error) {
          setStatus('Sorry, this code is not available.', true);
          button.disabled = false;
        }
      });
    });
  };

  const welcomeOfferClaimKey = 'zovaWelcomeOfferClaimed';
  const welcomeOfferCookieKey = 'zova_welcome_offer_claimed';

  const hasClaimedWelcomeOffer = () => {
    try {
      if (window.localStorage.getItem(welcomeOfferClaimKey)) return true;
    } catch (error) {}

    return document.cookie
      .split(';')
      .some((cookie) => cookie.trim().startsWith(`${welcomeOfferCookieKey}=`));
  };

  const markWelcomeOfferClaimed = (source) => {
    const claim = JSON.stringify({ source, claimedAt: new Date().toISOString() });
    try {
      window.localStorage.setItem(welcomeOfferClaimKey, claim);
      window.sessionStorage.setItem('zovaOfferPopupSeen', 'true');
      window.sessionStorage.setItem('zovaLeadPopupSeen', 'true');
    } catch (error) {}

    document.cookie = `${welcomeOfferCookieKey}=true; Max-Age=31536000; Path=/; SameSite=Lax`;
  };

  const hideWelcomeOfferPopups = (except = null) => {
    document.querySelectorAll('[data-offer-popup], [data-lead-popup]').forEach((popup) => {
      if (popup !== except) popup.hidden = true;
    });
  };

  const setupOfferPopup = (root = document) => {
    if (document.body.classList.contains('template-inside-zova')) return;

    const popup = root.querySelector?.('[data-offer-popup]') || document.querySelector('[data-offer-popup]');
    if (!popup || popup.dataset.offerPopupReady === 'true') return;
    popup.dataset.offerPopupReady = 'true';

    const storageKey = 'zovaOfferPopupSeen';
    const closeButton = popup.querySelector('[data-offer-popup-close]');
    const claimLinks = popup.querySelectorAll('[data-welcome-offer-claim]');

    const closePopup = () => {
      popup.hidden = true;
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (error) {}
    };

    const showPopup = () => {
      if (hasClaimedWelcomeOffer()) return;
      hideWelcomeOfferPopups(popup);
      popup.hidden = false;
    };

    if (closeButton) closeButton.addEventListener('click', closePopup);
    claimLinks.forEach((link) => {
      link.addEventListener('click', () => {
        markWelcomeOfferClaimed(link.dataset.welcomeOfferClaim || 'visual-offer');
        hideWelcomeOfferPopups();
      });
    });

    let hasSeen = false;
    try {
      hasSeen = window.sessionStorage.getItem(storageKey) === 'true';
    } catch (error) {}

    if (!hasSeen && !hasClaimedWelcomeOffer()) {
      window.setTimeout(showPopup, 7000);
    }
  };

  const setupLeadPopup = (root = document) => {
    if (document.body.classList.contains('template-inside-zova')) return;

    const popup = root.querySelector?.('[data-lead-popup]') || document.querySelector('[data-lead-popup]');
    if (!popup || popup.dataset.leadPopupReady === 'true') return;
    popup.dataset.leadPopupReady = 'true';

    const storageKey = 'zovaLeadPopupSeen';
    const closeButton = popup.querySelector('[data-lead-popup-close]');
    const form = popup.querySelector('[data-lead-popup-form]');
    const input = popup.querySelector('[data-lead-popup-input]');
    const status = popup.querySelector('[data-lead-popup-status]');
    const submitButton = form?.querySelector('[type="submit"]');
    const offerUrl = popup.dataset.welcomeOfferUrl || '/discount/ZOVA10?redirect=/collections/all';

    const closePopup = () => {
      popup.hidden = true;
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch (error) {}
    };

    const showPopup = () => {
      if (hasClaimedWelcomeOffer()) return;
      const offerPopup = document.querySelector('[data-offer-popup]');
      if (offerPopup && !offerPopup.hidden) {
        window.setTimeout(showPopup, 4000);
        return;
      }
      popup.hidden = false;
    };

    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const isValidIndianPhone = (value) => {
      const digits = value.replace(/\D/g, '');
      if (digits.length === 10) return /^[6-9]\d{9}$/.test(digits);
      if (digits.length === 12 && digits.startsWith('91')) return /^[6-9]\d{9}$/.test(digits.slice(2));
      return false;
    };

    if (closeButton) closeButton.addEventListener('click', closePopup);

    if (form && input && status) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const value = input.value.trim();
        const isValid = isValidEmail(value) || isValidIndianPhone(value);

        status.hidden = false;
        status.classList.toggle('is-error', !isValid);

        if (!isValid) {
          status.textContent = 'Please enter a valid email or Indian phone number.';
          return;
        }

        status.textContent = 'Your 10% welcome offer is activated. Opening the shop…';
        if (submitButton) submitButton.disabled = true;
        markWelcomeOfferClaimed('contact-offer');
        hideWelcomeOfferPopups(popup);
        window.setTimeout(() => window.location.assign(offerUrl), 650);
      });
    }

    let hasSeen = false;
    try {
      hasSeen = window.sessionStorage.getItem(storageKey) === 'true';
    } catch (error) {}

    if (!hasSeen && !hasClaimedWelcomeOffer()) {
      window.setTimeout(showPopup, 15000);
    }
  };

  const removeDeferredPurchaseNotices = (root = document) => {
    const noticeText = 'This item is a deferred, subscription, or recurring purchase';
    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll('p, div, span, small').forEach((node) => {
      if (node.children.length > 0) return;
      if (!node.textContent.includes(noticeText)) return;
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
    });
  };

  setupHomeHeroSliders();
  setupFreshScrollSections();
  setupHomeInstagramReels();
  setupHomeReviews();
  setupHomeValues();
  setupInsideStory();
  setupHomeVideoStories();
  setupShopProductSearches();
  setupShopCategoryFilters();
  setupZovaFaq();
  setupShopShowcaseGalleries();
  setupReachForms();
  setupCartDiscountForms();
  setupOfferPopup();
  setupLeadPopup();
  removeDeferredPurchaseNotices();
  document.addEventListener('shopify:section:load', (event) => {
    setupHomeHeroSliders(event.target);
    setupFreshScrollSections(event.target);
    setupHomeInstagramReels(event.target);
    setupHomeReviews(event.target);
    setupHomeValues(event.target);
    setupInsideStory(event.target);
    setupHomeVideoStories(event.target);
    setupShopProductSearches(event.target);
    setupShopCategoryFilters(event.target);
    setupZovaFaq(event.target);
    setupShopShowcaseGalleries(event.target);
    setupReachForms(event.target);
    setupCartDiscountForms(event.target);
    setupOfferPopup(event.target);
    setupLeadPopup(event.target);
    removeDeferredPurchaseNotices(event.target);
  });

  window.setTimeout(removeDeferredPurchaseNotices, 600);
  window.setTimeout(removeDeferredPurchaseNotices, 1600);
});
